import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Activity,
  BookOpen,
  ChartNoAxesCombined,
  FlaskConical,
  Gauge,
  Github,
  Play,
  RefreshCw,
  SlidersHorizontal,
  Square,
  X,
} from 'lucide-react'
import { KpiGrid } from './components/KpiGrid'
import { ParameterPanel } from './components/ParameterPanel'
import { VolatilityHeatmap } from './components/VolatilityHeatmap'
import { DEFAULTS, inputsKey } from './lib/defaults'
import { validateSimulationInputs } from './lib/monteCarlo'
import { loadInitialInputs, saveInputs, shareUrl, syncUrl } from './lib/persistence'
import { getStrategyMeta, needsTailRiskWarning } from './lib/strategy'
import { useOptimization } from './hooks/useOptimization'
import { useSimulationJob } from './hooks/useSimulationJob'
import type { SimulationInputs } from './types'

// Charts pull in Recharts (~400 kB); load them in parallel with the first simulation instead of before first paint.
const loadPathPlayback = () => import('./components/PathPlayback')
const loadSimulationCharts = () => import('./components/SimulationCharts')
const PathPlayback = lazy(() => loadPathPlayback().then((m) => ({ default: m.PathPlayback })))
const SimulationCharts = lazy(() => loadSimulationCharts().then((m) => ({ default: m.SimulationCharts })))
const ScenarioTab = lazy(() => import('./components/ScenarioTab').then((m) => ({ default: m.ScenarioTab })))
const StrategyComparison = lazy(() => import('./components/StrategyComparison').then((m) => ({ default: m.StrategyComparison })))
const ParameterOptimization = lazy(() => import('./components/ParameterOptimization').then((m) => ({ default: m.ParameterOptimization })))
const Methodology = lazy(() => import('./components/Methodology').then((m) => ({ default: m.Methodology })))

type Tab = 'simulator' | 'scenario' | 'comparison' | 'optimization' | 'methodology'

const TABS: { key: Tab; label: string; icon: ReactNode }[] = [
  { key: 'simulator', label: 'Simulator', icon: <Gauge size={15} /> },
  { key: 'scenario', label: 'Scenario', icon: <FlaskConical size={15} /> },
  { key: 'comparison', label: 'Compare', icon: <ChartNoAxesCombined size={15} /> },
  { key: 'optimization', label: 'Optimize', icon: <SlidersHorizontal size={15} /> },
  { key: 'methodology', label: 'Method', icon: <BookOpen size={15} /> },
]

const MOBILE_QUERY = '(max-width: 900px)'

function isValid(inputs: SimulationInputs) {
  try {
    validateSimulationInputs(inputs)
    return true
  } catch {
    return false
  }
}

function TabFallback() {
  return (
    <div className="loading-state">
      <div className="spinner" />
      <strong>加载中…</strong>
    </div>
  )
}

function App() {
  const [inputs, setInputs] = useState<SimulationInputs>(loadInitialInputs)
  const [tab, setTab] = useState<Tab>('simulator')
  const [visited, setVisited] = useState<Set<Tab>>(() => new Set(['simulator']))
  const [panelOpen, setPanelOpen] = useState(false)
  const [toast, setToast] = useState('')
  const simulation = useSimulationJob<'simulate', undefined>('simulate')
  const comparison = useSimulationJob<'compare', string>('compare')
  const heatmap = useSimulationJob<'heatmap', string>('heatmap')
  const optimization = useOptimization(inputs)
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({})

  const result = simulation.data?.value ?? null
  const currentKey = useMemo(() => inputsKey(inputs), [inputs])
  const resultKey = useMemo(() => (result ? inputsKey(result.inputs) : ''), [result])
  const simulationStale = Boolean(result) && resultKey !== currentKey
  const comparisonStale = Boolean(comparison.data) && comparison.data!.context !== currentKey
  // The heatmap is always computed from the displayed result's inputs, never the (possibly edited) panel.
  const heatmapCells = heatmap.data && heatmap.data.context === resultKey ? heatmap.data.value : []
  const displayed = result?.inputs ?? inputs
  const meta = getStrategyMeta(displayed.strategy)

  const updateInputs = useCallback((patch: Partial<SimulationInputs>) => {
    setInputs((current) => ({ ...current, ...patch }))
  }, [])

  const run = useCallback((next: SimulationInputs = inputs) => {
    if (!isValid(next)) return
    heatmap.cancel()
    simulation.run({ inputs: next }, undefined)
    if (window.matchMedia(MOBILE_QUERY).matches) setPanelOpen(false)
  }, [inputs, simulation.run, heatmap.cancel])

  const selectTab = (next: Tab) => {
    setTab(next)
    setVisited((current) => (current.has(next) ? current : new Set(current).add(next)))
  }

  const onTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    if (!delta && event.key !== 'Home' && event.key !== 'End') return
    event.preventDefault()
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? TABS.length - 1 : (index + delta + TABS.length) % TABS.length
    const next = TABS[nextIndex].key
    selectTab(next)
    tabRefs.current[next]?.focus()
  }

  // Auto-run once on load, and warm the chart chunks while the engine works.
  useEffect(() => {
    void loadPathPlayback()
    void loadSimulationCharts()
    const timer = window.setTimeout(() => run(), 60)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist the panel and mirror it into a shareable URL.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      saveInputs(inputs)
      syncUrl(inputs)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [inputs])

  // Ctrl/⌘ + Enter runs the simulation from anywhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        run()
      }
      if (event.key === 'Escape') setPanelOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [run])

  // Lock page scroll behind the mobile parameter drawer; close it if the viewport grows.
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY)
    const locked = panelOpen && query.matches
    document.body.classList.toggle('scroll-locked', locked)
    const onChange = () => { if (!query.matches) setPanelOpen(false) }
    query.addEventListener('change', onChange)
    return () => {
      document.body.classList.remove('scroll-locked')
      query.removeEventListener('change', onChange)
    }
  }, [panelOpen])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(''), 2400)
    return () => window.clearTimeout(timer)
  }, [toast])

  const share = async () => {
    const url = shareUrl(inputs)
    try {
      await navigator.clipboard.writeText(url)
      setToast('链接已复制，打开即可复现当前参数')
    } catch {
      window.prompt('复制此链接以分享当前参数', url)
    }
  }

  const percentileStrip = useMemo(() => {
    if (!result) return []
    const s = result.summary
    return [
      ['P05', s.p05],
      ['P25', s.p25],
      ['P50', s.p50],
      ['P75', s.p75],
      ['P95', s.p95],
      ['ES 5%', s.expectedShortfall05],
    ] as const
  }, [result])

  const errors = [
    { key: 'simulation', message: simulation.error, dismiss: simulation.dismissError },
    { key: 'comparison', message: comparison.error, dismiss: comparison.dismissError },
    { key: 'heatmap', message: heatmap.error, dismiss: heatmap.dismissError },
  ].filter((entry) => entry.message)

  const strikeLabel = (source: SimulationInputs) =>
    source.strikeMethod === 'delta' ? source.targetDelta.toFixed(2) + '|Δ|' : source.strikeMethod.toUpperCase()

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到结果</a>
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark"><Activity size={18} /></div>
          <div>
            <strong>Option Strategy Lab</strong>
            <span>Multi-Leg Monte Carlo Simulator</span>
          </div>
        </div>
        <nav role="tablist" aria-label="功能页">
          {TABS.map((entry, index) => (
            <button
              key={entry.key}
              ref={(node) => { tabRefs.current[entry.key] = node }}
              role="tab"
              id={'tab-' + entry.key}
              aria-selected={tab === entry.key}
              aria-controls="main-content"
              tabIndex={tab === entry.key ? 0 : -1}
              className={tab === entry.key ? 'active' : ''}
              onClick={() => selectTab(entry.key)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
            >
              {entry.icon} <span>{entry.label}</span>
            </button>
          ))}
        </nav>
        <a className="github-link" href="https://github.com/fatherle/covered-call-simulator" target="_blank" rel="noreferrer" aria-label="GitHub 仓库">
          <Github size={16} /> <span>GitHub</span>
        </a>
      </header>

      <div className="workspace">
        {panelOpen && <div className="panel-backdrop" onClick={() => setPanelOpen(false)} aria-hidden="true" />}
        <ParameterPanel
          inputs={inputs}
          running={simulation.running}
          progress={simulation.progress}
          stale={simulationStale}
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          onChange={updateInputs}
          onRun={() => run()}
          onCancel={simulation.cancel}
          onReset={() => setInputs(DEFAULTS)}
          onRandomizeSeed={() => updateInputs({ seed: Math.floor(Math.random() * 2_000_000_000) + 1 })}
          onShare={share}
        />

        <main className="main-panel" id="main-content" role="tabpanel" aria-labelledby={'tab-' + tab} tabIndex={-1}>
          {errors.map((entry) => (
            <div className="error-banner" role="alert" key={entry.key}>
              <span>{entry.message}</span>
              <button className="banner-close" onClick={entry.dismiss} aria-label="关闭错误提示"><X size={14} /></button>
            </div>
          ))}

          {tab === 'simulator' && (
            <>
              <section className="hero-strip">
                <div>
                  <p className="eyebrow">CURRENT STRATEGY</p>
                  <h1>{meta.label}</h1>
                </div>
                <div className="hero-metric">
                  <span>IV / Diff σ</span>
                  <strong>{(displayed.impliedVol * 100).toFixed(0)}% / {(displayed.realizedVol * 100).toFixed(0)}%</strong>
                </div>
                <div className="hero-metric">
                  <span>DTE / Strike</span>
                  <strong>{displayed.dte}D · {strikeLabel(displayed)}</strong>
                </div>
                <div className="hero-metric">
                  <span>Settlement / Margin</span>
                  <strong>
                    {displayed.settlementStyle === 'cash' ? 'Cash' : 'Physical'} · {displayed.marginMode === 'off' ? 'Off' : 'Reg-T'}
                  </strong>
                </div>
              </section>

              {simulationStale && (
                <div className="stale-banner" role="status">
                  <span>参数已修改：下方结果仍基于上一次运行。</span>
                  {simulation.running ? (
                    <span>重新计算中 {simulation.progress}%</span>
                  ) : (
                    <button className="play-small" onClick={() => run()} disabled={!isValid(inputs)}>
                      <RefreshCw size={13} /> 重新运行
                    </button>
                  )}
                </div>
              )}

              {needsTailRiskWarning(displayed.strategy) && (
                <div className="risk-banner">
                  <strong>Margin / tail-risk model:</strong> {meta.risk}
                  <span>
                    {displayed.marginMode === 'reg_t_approx'
                      ? '已启用 Reg-T 研究近似；触发时会强制平仓。实际券商要求可能更高，也可能采用不同的组合抵扣。'
                      : '当前为纯经济 P&amp;L 模式，不模拟券商保证金/强平；负权益概率不是实际爆仓概率。'}
                  </span>
                </div>
              )}

              {result ? (
                <div className={simulationStale ? 'results is-stale' : 'results'}>
                  <KpiGrid result={result} />
                  <div className="percentile-strip">
                    {percentileStrip.map(([label, value]) => (
                      <span key={label}>
                        <small>{label}</small>
                        <strong className={value >= 0 ? 'positive' : 'negative'}>{value >= 0 ? '+' : ''}{(value * 100).toFixed(1)}%</strong>
                      </span>
                    ))}
                    <span>
                      <small>MC SE</small>
                      <strong>±{(result.summary.meanReturnStandardError * 100).toFixed(2)}%</strong>
                    </span>
                    <span>
                      <small>Engine</small>
                      <strong>{(result.elapsedMs / 1000).toFixed(2)}s</strong>
                    </span>
                  </div>
                  <Suspense fallback={<div className="chart-card chart-card--wide chart-skeleton" aria-busy="true">加载图表…</div>}>
                    <PathPlayback paths={result.samples} inputs={result.inputs} />
                    <SimulationCharts result={result} />
                  </Suspense>
                  <VolatilityHeatmap
                    inputs={result.inputs}
                    cells={heatmapCells}
                    running={heatmap.running}
                    progress={heatmap.progress}
                    onRun={(ivs, rvs) => heatmap.run({ inputs: result.inputs, ivs, rvs }, resultKey)}
                    onCancel={heatmap.cancel}
                  />
                </div>
              ) : (
                <div className="loading-state">
                  {simulation.running ? (
                    <>
                      <div className="spinner" />
                      <strong>Monte Carlo 模拟中… {simulation.progress}%</strong>
                    </>
                  ) : (
                    <>
                      <strong>尚无结果</strong>
                      <button className="run-button run-button--compact" onClick={() => run()} disabled={!isValid(inputs)}>
                        <Play size={14} fill="currentColor" /> 运行模拟
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <Suspense fallback={<TabFallback />}>
            {tab === 'scenario' && <ScenarioTab inputs={inputs} />}
            {tab === 'comparison' && (
              <StrategyComparison
                base={inputs}
                results={comparison.data?.value ?? []}
                stale={comparisonStale}
                running={comparison.running}
                progress={comparison.progress}
                onRun={(strategies) => comparison.run({ strategies }, currentKey)}
                onCancel={comparison.cancel}
                onApply={(selected) => {
                  setInputs(selected)
                  selectTab('simulator')
                  run(selected)
                }}
              />
            )}
            {visited.has('optimization') && (
              <div hidden={tab !== 'optimization'}>
                <ParameterOptimization
                  base={inputs}
                  result={optimization.result}
                  running={optimization.running}
                  progress={optimization.progress}
                  completed={optimization.completed}
                  total={optimization.total}
                  error={optimization.error}
                  onRun={optimization.run}
                  onCancel={optimization.cancel}
                  onApply={(selected) => {
                    setInputs(selected)
                    selectTab('simulator')
                    run(selected)
                  }}
                />
              </div>
            )}
            {tab === 'methodology' && <Methodology />}
          </Suspense>
        </main>
      </div>

      <div className="mobile-bar" role="toolbar" aria-label="模拟操作">
        <button
          className="mobile-bar__params"
          onClick={() => setPanelOpen(true)}
          aria-controls="parameter-panel"
          aria-expanded={panelOpen}
        >
          <SlidersHorizontal size={16} />
          <span>
            <strong>参数{simulationStale ? ' · 已修改' : ''}</strong>
            <small>{getStrategyMeta(inputs.strategy).shortLabel} · {inputs.dte}D · {strikeLabel(inputs)}</small>
          </span>
        </button>
        {simulation.running ? (
          <button className="run-button run-button--cancel mobile-bar__run" onClick={simulation.cancel}>
            <Square size={13} fill="currentColor" /> {simulation.progress}%
          </button>
        ) : (
          <button className="run-button mobile-bar__run" onClick={() => run()} disabled={!isValid(inputs)}>
            <Play size={14} fill="currentColor" /> 运行
          </button>
        )}
      </div>

      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  )
}

export default App
